import React, { useState, useEffect } from 'react';
import { Offcanvas, Tab, Tabs, Form, Button, Card, Modal, Badge, Alert, Carousel, Table } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import { getBiddingItems, createBiddingItem, updateBiddingItem, uploadBiddingPhoto, deleteBiddingPhoto, placeBid, verifyBidOtp, getBiddingCategories, createBiddingCategory, getAllBids, exportBids, getAdminItems, exportBiddingItems, closeAuction, verifyWinnerOtp, getAnonymousBids, approveBidItem } from '../../../services/biddingService';
import { getProcurementSettings } from '../../../models/procurementModel';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import MultiFileInput from '../../../components/common/MultiFileInput';
import WysiwygEditor from '../../../components/common/WysiwygEditor';
import { format, parse } from 'date-fns';

import { colors } from '../../../utils/colors';

const BiddingPanel = ({ show, onHide }) => {
    const { user, can, isProcurement } = useAuth();
    const [key, setKey] = useState('items');
    const [items, setItems] = useState([]);
    const [adminItems, setAdminItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [submitting, setSubmitting] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [notifyOutbid, setNotifyOutbid] = useState(false);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [biddingSettings, setBiddingSettings] = useState(null);
    const [hasAcceptedDisclaimerForItem, setHasAcceptedDisclaimerForItem] = useState(false);
    
    // All Bids State (anonymous view)
    const [allBids, setAllBids] = useState([]);
    const [bidsLoading, setBidsLoading] = useState(false);
    const [bidFilters, setBidFilters] = useState({
        item_id: '',
        status: '',
        date_from: '',
        date_to: ''
    });
    const [exporting, setExporting] = useState(false);
    const [exportingItems, setExportingItems] = useState(false);
    const [itemSearchQuery, setItemSearchQuery] = useState('');
    
    // Winner OTP Verification State
    const [showWinnerOtpModal, setShowWinnerOtpModal] = useState(false);
    const [winnerOtp, setWinnerOtp] = useState('');
    const [selectedWinnerItem, setSelectedWinnerItem] = useState(null);
    const [verifyingWinner, setVerifyingWinner] = useState(false);
    const [revealedWinner, setRevealedWinner] = useState(null);
    const [closingAuction, setClosingAuction] = useState(null);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    
    // Create Form State
    const [newItem, setNewItem] = useState({
        title: '',
        category_id: '',
        description: '',
        opening_bid: '',
        increment_amount: '',
        opening_date: '',
        closing_date: '',
        status: 'open'
    });
    const [files, setFiles] = useState([]);
    const [existingPhotos, setExistingPhotos] = useState([]);
    const [editingId, setEditingId] = useState(null);

    // Bid State
    const [selectedItem, setSelectedItem] = useState(null);
    const [bidAmount, setBidAmount] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState('');
    const [currentBidId, setCurrentBidId] = useState(null);
    const [now, setNow] = useState(new Date());
    const [viewingImagesItem, setViewingImagesItem] = useState(null);

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const getTimeRemaining = (targetDate) => {
        const total = Date.parse(targetDate) - Date.parse(now);
        if (total <= 0) return null;
        const minutes = Math.floor((total / 1000 / 60) % 60);
        const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
        const days = Math.floor(total / (1000 * 60 * 60 * 24));
        
        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    useEffect(() => {
        if (show) {
            fetchItems();
            fetchCategories();
            fetchBiddingSettings();
            if (canAdminBidding) {
                fetchAdminItems();
            }
            if (key === 'create') {
                fetchSettings();
            }
        }
    }, [show, key]);

    const fetchAdminItems = async () => {
        try {
            const data = await getAdminItems();
            if (data.status === 1) {
                setAdminItems(data.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCategories = async () => {
        try {
            const data = await getBiddingCategories();
            if (data.status === 1) {
                setCategories(data.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        try {
            const res = await createBiddingCategory({ name: newCategory });
            if (res.status === 1) {
                toast.success('Category created');
                setNewCategory('');
                fetchCategories();
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error('Error creating category');
        }
    };

    const fetchItems = async () => {
        setLoading(true);
        try {
            const data = await getBiddingItems();
            if (data.status === 1) {
                setItems(data.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const settings = await getProcurementSettings();
            if (settings) {
                setNewItem(prev => ({
                    ...prev,
                    opening_date: settings.site_bid_opening || prev.opening_date,
                    closing_date: settings.site_bid_closing || prev.closing_date
                }));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchBiddingSettings = async () => {
        try {
            const settings = await getProcurementSettings();
            if (settings) {
                setBiddingSettings(settings);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (creating) return;
        
        setCreating(true);
        try {
            let res;
            if (editingId) {
                res = await updateBiddingItem(editingId, newItem);
            } else {
                res = await createBiddingItem(newItem);
            }

            if (res.status === 1) {
                if (files.length > 0) {
                    const itemId = editingId || res.data.id;
                    for (const file of files) {
                        const uploadRes = await uploadBiddingPhoto(itemId, file);
                        if (uploadRes.status !== 1) {
                            toast.error(`Failed to upload ${file.name}: ${uploadRes.message}`);
                        }
                    }
                }
                toast.success(editingId ? 'Item updated' : 'Item created');
                setNewItem({ title: '', category_id: '', description: '', opening_bid: '', increment_amount: '', opening_date: '', closing_date: '', status: 'open' });
                setFiles([]);
                setExistingPhotos([]);
                setEditingId(null);
                setKey('items');
                fetchAdminItems();
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error(editingId ? 'Error updating item' : 'Error creating item');
        } finally {
            setCreating(false);
        }
    };

    const handleEdit = (item) => {
        setNewItem({
            title: item.title,
            category_id: item.category_id,
            description: item.description,
            opening_bid: item.opening_bid,
            increment_amount: item.increment_amount,
            opening_date: item.opening_date,
            closing_date: item.closing_date,
            status: item.status
        });
        setExistingPhotos(item.photos || []);
        setFiles([]); // Clear any previously selected files
        setEditingId(item.id);
        setKey('create');
    };

    const handleDeletePhoto = async (photoId) => {
        if (!window.confirm('Are you sure you want to delete this photo?')) return;
        try {
            const res = await deleteBiddingPhoto(photoId);
            if (res.status === 1) {
                setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
                toast.success('Photo deleted');
                fetchItems(); // Refresh items to update the list view as well
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error('Error deleting photo');
        }
    };

    const handleBidClick = (item) => {
        setSelectedItem(item);
        
        const currentHighest = parseFloat(item.highest_bid) || 0;
        const openingBid = parseFloat(item.opening_bid) || 0;
        const increment = parseFloat(item.increment_amount) || 0;
        
        let nextBid = 0;
        if (currentHighest > 0) {
            nextBid = currentHighest + increment;
        } else {
            nextBid = openingBid;
        }
        
        setBidAmount(nextBid.toFixed(2));
        setNotifyOutbid(false);
        
        // Check if user has already accepted disclaimer for this item
        const hasAccepted = item.my_bid && item.my_bid.disclaimer_accepted === 1;
        setHasAcceptedDisclaimerForItem(hasAccepted);
        setDisclaimerAccepted(hasAccepted); // Pre-check if already accepted
    };

    const handleBidSubmit = async (e) => {
        e.preventDefault();
        if (!selectedItem || submitting) return;
        
        setSubmitting(true);
        try {
            const res = await placeBid({ 
                item_id: selectedItem.id, 
                amount: bidAmount,
                notify_outbid: notifyOutbid ? 1 : 0,
                disclaimer_accepted: disclaimerAccepted ? 1 : 0
            });
            if (res.status === 1) {
                setCurrentBidId(res.data.bid_id);
                setShowOtpModal(true);
                toast.success(res.message || 'OTP sent to your phone');
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error('Error placing bid');
        } finally {
            setSubmitting(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (verifying) return;
        setVerifying(true);
        try {
            const res = await verifyBidOtp({ bid_id: currentBidId, otp });
            if (res.status === 1) {
                toast.success('Bid confirmed');
                setShowOtpModal(false);
                setSelectedItem(null);
                setBidAmount('');
                setOtp('');
                fetchItems();
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error('Error verifying OTP');
        } finally {
            setVerifying(false);
        }
    };

    const handleDateChange = (field, dateStr) => {
        if (!dateStr) {
             setNewItem({ ...newItem, [field]: '' });
             return;
        }

        const parsedDate = parse(dateStr, 'dd/MM/yyyy', new Date());
        const datePart = format(parsedDate, 'yyyy-MM-dd');
        
        let timePart = '12:00';
        if (newItem[field]) {
            const current = new Date(newItem[field]);
            if (!isNaN(current)) {
                timePart = format(current, 'HH:mm');
            }
        }
        
        setNewItem({ ...newItem, [field]: `${datePart}T${timePart}` });
    };

    const handleTimeChange = (field, timeValue) => {
        const currentDateTime = newItem[field];
        let datePart = format(new Date(), 'yyyy-MM-dd');
        
        if (currentDateTime) {
             const dateObj = new Date(currentDateTime);
             if (!isNaN(dateObj)) {
                 datePart = format(dateObj, 'yyyy-MM-dd');
             }
        }
        
        setNewItem({ ...newItem, [field]: `${datePart}T${timeValue}` });
    };

    // Check if user can administer bidding - permission-based access
    // Users with these permissions can create/edit items and manage categories
    const canAdminBidding = isProcurement() || can('Create Bidding Item') || can('Edit Bidding Item') || can('Manage Bidding Categories');
    const canCreateItem = isProcurement() || can('Create Bidding Item');
    const canEditItem = isProcurement() || can('Edit Bidding Item');
    const canManageCategories = isProcurement() || can('Manage Bidding Categories');
    const canViewAllBids = isProcurement() || can('View All Bids');
    const canApproveBidItems = can('Approve Bidding Items');

    // Fetch all bids for admin view (anonymous - no personal details)
    const fetchAllBids = async () => {
        setBidsLoading(true);
        try {
            const data = await getAnonymousBids(bidFilters);
            if (data.status === 1) {
                setAllBids(data.data || []);
            } else {
                toast.error(data.message || 'Failed to load bids');
            }
        } catch (error) {
            console.error('Error fetching bids:', error);
            toast.error('Error loading bids');
        } finally {
            setBidsLoading(false);
        }
    };
    
    // Handle closing an auction
    const handleCloseAuction = async (itemId) => {
        if (!window.confirm('Are you sure you want to close this auction? An OTP will be sent to the highest bidder.')) {
            return;
        }
        
        setClosingAuction(itemId);
        try {
            const res = await closeAuction(itemId);
            if (res.status === 1) {
                toast.success(res.message || 'Auction closed successfully');
                fetchAdminItems();
                fetchItems();
            } else {
                toast.error(res.message || 'Failed to close auction');
            }
        } catch (error) {
            toast.error('Error closing auction');
        } finally {
            setClosingAuction(null);
        }
    };
    
    // Handle approving a bid item
    const handleApproveItem = async (itemId) => {
        const { default: Swal } = await import('sweetalert2');
        
        const result = await Swal.fire({
            title: 'Approve Bid Item',
            text: 'Are you sure you want to approve this item for external bidding?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: colors.primary,
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, approve it!',
            cancelButtonText: 'Cancel'
        });
        
        if (result.isConfirmed) {
            try {
                const res = await approveBidItem(itemId);
                if (res.status === 1) {
                    await Swal.fire({
                        title: 'Approved!',
                        text: 'The bid item has been approved for external bidding.',
                        icon: 'success',
                        confirmButtonColor: colors.primary
                    });
                    fetchAdminItems();
                    fetchItems();
                } else {
                    toast.error(res.message || 'Failed to approve item');
                }
            } catch (error) {
                toast.error('Error approving item');
            }
        }
    };
    
    // Handle verifying winner OTP
    const handleVerifyWinnerOtp = async () => {
        if (!selectedWinnerItem || !winnerOtp) return;
        
        setVerifyingWinner(true);
        try {
            const res = await verifyWinnerOtp(selectedWinnerItem.id, winnerOtp);
            if (res.status === 1) {
                toast.success('Winner verified successfully');
                setRevealedWinner(res.data.winner);
                fetchAdminItems();
            } else {
                toast.error(res.message || 'Invalid OTP');
            }
        } catch (error) {
            toast.error('Error verifying OTP');
        } finally {
            setVerifyingWinner(false);
        }
    };
    
    // Open winner verification modal
    const openWinnerVerification = (item) => {
        setSelectedWinnerItem(item);
        setWinnerOtp('');
        setRevealedWinner(null);
        setShowWinnerOtpModal(true);
    };

    // Handle bid export
    const handleExportBids = async () => {
        setExporting(true);
        try {
            const blob = await exportBids(bidFilters);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `bids_export_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Bids exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export bids');
        } finally {
            setExporting(false);
        }
    };

    // Handle items export
    const handleExportItems = async () => {
        setExportingItems(true);
        try {
            const blob = await exportBiddingItems();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `bidding_items_export_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Items exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export items');
        } finally {
            setExportingItems(false);
        }
    };

    // Load bids when switching to all_bids tab
    useEffect(() => {
        if (show && key === 'all_bids' && canViewAllBids) {
            fetchAllBids();
        }
    }, [show, key, bidFilters]);

    const renderItemsList = (itemsToRender, emptyMessage) => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            );
        }

        const filteredItems = selectedCategory === 'all' 
            ? itemsToRender 
            : itemsToRender.filter(i => i.category_id == selectedCategory);

        return (
            <div className="d-flex flex-column gap-3">
                {/* Category Filters */}
                <div className="d-flex gap-2 overflow-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                    <Button 
                        variant={selectedCategory === 'all' ? 'primary' : 'light'} 
                        size="sm" 
                        className="rounded-pill px-3 flex-shrink-0"
                        style={selectedCategory === 'all' ? { backgroundColor: colors.primary, borderColor: colors.primary } : {}}
                        onClick={() => setSelectedCategory('all')}
                    >
                        All
                    </Button>
                    {categories.map(cat => (
                        <Button 
                            key={cat.id} 
                            variant={selectedCategory == cat.id ? 'primary' : 'light'} 
                            size="sm" 
                            className="rounded-pill px-3 flex-shrink-0"
                            style={selectedCategory == cat.id ? { backgroundColor: colors.primary, borderColor: colors.primary } : {}}
                            onClick={() => setSelectedCategory(cat.id)}
                        >
                            {cat.name}
                        </Button>
                    ))}
                </div>

                {filteredItems.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <p className="mb-0">{emptyMessage}</p>
                    </div>
                ) : (
                    <div className="row g-3">
                        {filteredItems.map(item => (
                            <div key={item.id} className="col-12 col-md-6">
                                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden rounded-xl bg-white h-100">
                                    {item.photos && item.photos.length > 0 && (
                                        <div className="position-relative" style={{ cursor: 'pointer' }} onClick={() => setViewingImagesItem(item)}>
                                            <Card.Img variant="top" src={`${API_BASE_URL}${item.photos[0].photo_path}`} style={{ height: '160px', objectFit: 'cover' }} />
                                            <div className="position-absolute top-0 end-0 m-2">
                                                <Badge bg="light" text="dark" className="shadow-sm">
                                                    #{item.id}
                                                </Badge>
                                            </div>
                                            {item.photos.length > 1 && (
                                                <div className="position-absolute bottom-0 end-0 m-2">
                                                    <Badge bg="dark" className="shadow-sm opacity-75">
                                                        <i className="fas fa-images me-1"></i> {item.photos.length}
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <Card.Body className="p-3 d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <Card.Title className="h6 font-bold text-dark mb-0 text-truncate" title={item.title}>{item.title}</Card.Title>
                                            <Badge bg={new Date(item.closing_date) < new Date() ? 'danger' : 'success'} pill className="ms-2 flex-shrink-0" style={{fontSize: '0.65rem'}}>
                                                {new Date(item.closing_date) < new Date() ? 'Closed' : 'Active'}
                                            </Badge>
                                        </div>
                                        
                                        <div 
                                            className="text-secondary mb-3 small flex-grow-1" 
                                            style={{ 
                                                display: '-webkit-box', 
                                                WebkitLineClamp: 3, 
                                                WebkitBoxOrient: 'vertical', 
                                                overflow: 'hidden' 
                                            }}
                                            dangerouslySetInnerHTML={{ __html: item.description }}
                                        />
                                        
                                        <div className="bg-light rounded p-2 mb-3 border border-gray-100 small">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className="text-muted">Opening Bid</span>
                                                <span className="font-semibold text-dark">R {parseFloat(item.opening_bid).toFixed(2)}</span>
                                            </div>
                                            {item.highest_bid && parseFloat(item.highest_bid) > 0 && (
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="text-muted">Current Highest</span>
                                                    <span className="font-bold text-primary">R {parseFloat(item.highest_bid).toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted">Closing</span>
                                                <span className="font-medium" style={{ color: colors.primary }}>{new Date(item.closing_date).toLocaleDateString()}</span>
                                            </div>
                                        </div>

                                        <div className="d-flex gap-2">
                                            {canEditItem && (
                                                <Button 
                                                    variant="light" 
                                                    size="sm"
                                                    className="border"
                                                    onClick={() => handleEdit(item)}
                                                    title="Edit Item"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </Button>
                                            )}

                                            {item.my_bid && parseFloat(item.my_bid.amount) >= parseFloat(item.highest_bid || 0) ? (
                                                <Alert variant="success" className="mb-0 border-0 bg-success-subtle text-success-emphasis p-2 small w-100">
                                                    <div className="d-flex align-items-center">
                                                        <i className="fas fa-check-circle me-2"></i>
                                                        <div>
                                                            <strong>Highest Bidder</strong><br/>
                                                            R {parseFloat(item.my_bid.amount).toFixed(2)}
                                                        </div>
                                                    </div>
                                                </Alert>
                                            ) : (
                                                <div className="d-flex flex-column gap-2 w-100">
                                                    {item.my_bid && (
                                                        <Alert variant="warning" className="mb-0 border-0 bg-warning-subtle text-warning-emphasis p-2 small w-100">
                                                            <div className="d-flex align-items-center">
                                                                <i className="fas fa-exclamation-circle me-2"></i>
                                                                <div>
                                                                    <strong>Outbid!</strong> Your bid: R {parseFloat(item.my_bid.amount).toFixed(2)}
                                                                </div>
                                                            </div>
                                                        </Alert>
                                                    )}
                                                    <Button 
                                                        variant="primary" 
                                                        size="sm"
                                                        className="w-100 fw-semibold shadow-sm"
                                                        style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
                                                        onClick={() => handleBidClick(item)}
                                                        disabled={new Date(item.closing_date) < now || (item.opening_date && new Date(item.opening_date) > now)}
                                                    >
                                                        {new Date(item.closing_date) < now ? 'Closed' : (item.opening_date && new Date(item.opening_date) > now ? `Opens in ${getTimeRemaining(item.opening_date)}` : (item.my_bid ? 'Bid Higher' : 'Place Bid'))}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '600px' }} className="border-l-0">
            <Offcanvas.Header className="text-white shadow-md d-block p-0" style={{ backgroundColor: colors.primary, minHeight: 'auto' }}>
                <div className="d-flex justify-content-between align-items-start p-3 pb-2">
                    <div>
                        <Offcanvas.Title className="font-bold tracking-wide text-white text-lg">Bidding Suite</Offcanvas.Title>
                        <p className="text-white text-opacity-75 text-xs mb-0">Manage your bids and procurement items</p>
                    </div>
                    <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={onHide}></button>
                </div>
                <div className="d-flex px-3 pb-3 gap-2 flex-wrap">
                    {/* Items Management Tab - for admins */}
                    {canAdminBidding && (
                        <div 
                            className={`px-3 py-1.5 rounded-md cursor-pointer text-sm font-medium transition-all duration-200 ${key === 'items' ? 'bg-white shadow-sm' : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10'}`}
                            style={key === 'items' ? { color: colors.primary } : {}}
                            onClick={() => setKey('items')}
                        >
                            <i className="fas fa-list me-1"></i>
                            Manage Items
                        </div>
                    )}
                    {canViewAllBids && (
                        <div 
                            className={`px-3 py-1.5 rounded-md cursor-pointer text-sm font-medium transition-all duration-200 ${key === 'all_bids' ? 'bg-white shadow-sm' : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10'}`}
                            style={key === 'all_bids' ? { color: colors.primary } : {}}
                            onClick={() => setKey('all_bids')}
                        >
                            <i className="fas fa-eye-slash me-1"></i>
                            All Bids
                        </div>
                    )}
                    {canAdminBidding && (
                        <>
                            <div 
                                className={`px-3 py-1.5 rounded-md cursor-pointer text-sm font-medium transition-all duration-200 ${key === 'create' ? 'bg-white shadow-sm' : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10'}`}
                                style={key === 'create' ? { color: colors.primary } : {}}
                                onClick={() => {
                                    setKey('create');
                                    if (editingId) {
                                        setEditingId(null);
                                        setNewItem({ title: '', category_id: '', description: '', opening_bid: '', increment_amount: '', opening_date: '', closing_date: '', status: 'open' });
                                        setFiles([]);
                                        setExistingPhotos([]);
                                    }
                                }}
                            >
                                {editingId && key === 'create' ? 'Edit Item' : '+ Create'}
                            </div>
                            <div 
                                className={`px-3 py-1.5 rounded-md cursor-pointer text-sm font-medium transition-all duration-200 ${key === 'categories' ? 'bg-white shadow-sm' : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10'}`}
                                style={key === 'categories' ? { color: colors.primary } : {}}
                                onClick={() => setKey('categories')}
                            >
                                Categories
                            </div>
                        </>
                    )}
                    {/* External Bidding Link */}
                    <a 
                        href="/bidding" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 text-white text-opacity-80 hover:bg-white hover:bg-opacity-10 text-decoration-none d-flex align-items-center"
                        title="Open External Bidding Portal in new tab"
                    >
                        <i className="fas fa-external-link-alt me-1"></i>
                        Bidding
                    </a>
                </div>
            </Offcanvas.Header>
            <Offcanvas.Body className="bg-light p-4">
                {/* Items Management Tab - CRUD list for admins */}
                {key === 'items' && canAdminBidding && (
                    <Card className="border-0 shadow-sm rounded-xl bg-white">
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="fw-bold mb-0"> 
                                    Items
                                </h5>
                                <div className="d-flex gap-2 flex-grow-1 mx-3">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by title, category, or ID..."
                                        value={itemSearchQuery}
                                        onChange={(e) => setItemSearchQuery(e.target.value)}
                                        className="shadow-sm"
                                        style={{ maxWidth: '300px' }}
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    {can('Export Bidding Data') && (
                                        <Button 
                                            variant="success" 
                                            size="sm"
                                            onClick={handleExportItems}
                                            disabled={exportingItems}
                                        >
                                            {exportingItems ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                    Exporting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download me-1"></i>
                                                    Export CSV
                                                </>
                                            )}
                                        </Button>
                                    )}
                                    <Button 
                                        variant="primary" 
                                        size="sm"
                                        style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
                                        onClick={() => {
                                            setEditingId(null);
                                            setNewItem({ title: '', category_id: '', description: '', opening_bid: '', increment_amount: '', opening_date: '', closing_date: '', status: 'open' });
                                            setFiles([]);
                                            setExistingPhotos([]);
                                            setKey('create');
                                        }}
                                    >
                                        <i className="fas fa-plus me-1"></i> Add Item
                                    </Button>
                                </div>
                            </div>
                            
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : adminItems.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                                    <p className="mb-0">No bidding items found.</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {adminItems.filter(item => {
                                        if (!itemSearchQuery) return true;
                                        const searchLower = itemSearchQuery.toLowerCase();
                                        return (
                                            item.title?.toLowerCase().includes(searchLower) ||
                                            item.category_name?.toLowerCase().includes(searchLower) ||
                                            item.id?.toString().includes(searchLower)
                                        );
                                    }).map(item => (
                                        <div key={item.id} className="border rounded-lg p-3 bg-light">
                                            {/* Item Photos */}
                                            {item.photos && item.photos.length > 0 && (
                                                <div className="mb-3 d-flex gap-2 overflow-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
                                                    {item.photos.map((photo, idx) => (
                                                        <img 
                                                            key={photo.id || idx}
                                                            src={`${API_BASE_URL}${photo.photo_path}`}
                                                            alt={`${item.title} photo ${idx + 1}`}
                                                            className="rounded border"
                                                            style={{ width: '80px', height: '80px', objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
                                                            onClick={() => setViewingImagesItem(item)}
                                                            title="Click to view larger"
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1 fw-bold">{item.title}</h6>
                                                    <small className="text-muted">
                                                        {item.category_name || 'Uncategorized'} • #{item.id}
                                                    </small>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    <Badge bg={item.status === 'open' ? 'success' : item.status === 'closed' ? 'secondary' : 'warning'}>
                                                        {item.status}
                                                    </Badge>
                                                    {item.auction_closed == 1 && (
                                                        <Badge bg="dark">Auction Closed</Badge>
                                                    )}
                                                    {item.is_approved == 1 ? (
                                                        <Badge bg="success">Approved</Badge>
                                                    ) : (
                                                        <Badge bg="warning" text="dark">Pending Approval</Badge>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Description with expander */}
                                            {item.description && (
                                                <div className="mb-3">
                                                    <div 
                                                        className="small text-secondary"
                                                        style={!expandedDescriptions[item.id] ? {
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        } : {}}
                                                        dangerouslySetInnerHTML={{ __html: item.description }}
                                                    />
                                                    {item.description.length > 100 && (
                                                        <button 
                                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                                            onClick={() => setExpandedDescriptions(prev => ({
                                                                ...prev,
                                                                [item.id]: !prev[item.id]
                                                            }))}
                                                        >
                                                            <small>
                                                                {expandedDescriptions[item.id] ? (
                                                                    <><i className="fas fa-chevron-up me-1"></i>Show less</>
                                                                ) : (
                                                                    <><i className="fas fa-chevron-down me-1"></i>Show more</>
                                                                )}
                                                            </small>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            
                                            <div className="row g-2 mb-3 small">
                                                <div className="col-4">
                                                    <span className="text-muted">Opening:</span><br/>
                                                    <strong>R {parseFloat(item.opening_bid || 0).toFixed(2)}</strong>
                                                </div>
                                                <div className="col-4">
                                                    <span className="text-muted">Highest Bid:</span><br/>
                                                    <strong className="text-primary">R {parseFloat(item.highest_bid || 0).toFixed(2)}</strong>
                                                </div>
                                                <div className="col-4">
                                                    <span className="text-muted">Bids:</span><br/>
                                                    <strong>{item.bid_count || 0}</strong>
                                                </div>
                                            </div>
                                            
                                            <div className="small text-muted mb-3">
                                                <i className="fas fa-calendar me-1"></i>
                                                Closes: {item.closing_date ? format(new Date(item.closing_date), 'dd MMM yyyy HH:mm') : 'Not set'}
                                            </div>
                                            
                                            <div className="d-flex gap-2 flex-wrap">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    <i className="fas fa-edit me-1"></i> Edit
                                                </Button>
                                                
                                                {canApproveBidItems && item.is_approved != 1 && (
                                                    <Button 
                                                        variant="outline-success" 
                                                        size="sm"
                                                        onClick={() => handleApproveItem(item.id)}
                                                    >
                                                        <i className="fas fa-check me-1"></i> Approve
                                                    </Button>
                                                )}
                                                
                                                {item.status === 'open' && !item.auction_closed && (
                                                    <Button 
                                                        variant="outline-danger" 
                                                        size="sm"
                                                        onClick={() => handleCloseAuction(item.id)}
                                                        disabled={closingAuction === item.id}
                                                    >
                                                        {closingAuction === item.id ? (
                                                            <><span className="spinner-border spinner-border-sm me-1"></span> Closing...</>
                                                        ) : (
                                                            <><i className="fas fa-gavel me-1"></i> Close Auction</>
                                                        )}
                                                    </Button>
                                                )}
                                                
                                                {item.auction_closed == 1 && item.winner_bid_id && (
                                                    <Button 
                                                        variant={item.winner_otp_verified ? 'success' : 'warning'} 
                                                        size="sm"
                                                        onClick={() => openWinnerVerification(item)}
                                                    >
                                                        <i className={`fas ${item.winner_otp_verified ? 'fa-check-circle' : 'fa-key'} me-1`}></i>
                                                        {item.winner_otp_verified ? 'View Winner' : 'Verify Winner'}
                                                    </Button>
                                                )}
                                                
                                                {item.auction_closed == 1 && !item.winner_bid_id && (
                                                    <Badge bg="secondary" className="py-2">No bids placed</Badge>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}

                {/* All Bids Tab - Admin View */}
                {key === 'all_bids' && canViewAllBids && (
                    <Card className="border-0 shadow-sm rounded-xl bg-white">
                        <Card.Body className="p-4">
                            {/* Filters */}
                            <div className="mb-4">
                                <h5 className="fw-bold mb-3">
                                    <i className="fas fa-filter me-2 text-muted"></i>
                                    Filter Bids
                                </h5>
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold">Item</Form.Label>
                                            <Form.Select 
                                                size="sm"
                                                value={bidFilters.item_id}
                                                onChange={(e) => setBidFilters({...bidFilters, item_id: e.target.value})}
                                            >
                                                <option value="">All Items</option>
                                                {items.map(item => (
                                                    <option key={item.id} value={item.id}>{item.title}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold">Status</Form.Label>
                                            <Form.Select 
                                                size="sm"
                                                value={bidFilters.status}
                                                onChange={(e) => setBidFilters({...bidFilters, status: e.target.value})}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="pending">Pending</option>
                                                <option value="confirmed">Confirmed</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold">From Date</Form.Label>
                                            <Form.Control 
                                                type="date" 
                                                size="sm"
                                                value={bidFilters.date_from}
                                                onChange={(e) => setBidFilters({...bidFilters, date_from: e.target.value})}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold">To Date</Form.Label>
                                            <Form.Control 
                                                type="date" 
                                                size="sm"
                                                value={bidFilters.date_to}
                                                onChange={(e) => setBidFilters({...bidFilters, date_to: e.target.value})}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="d-flex gap-2 mt-3">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => setBidFilters({ item_id: '', status: '', date_from: '', date_to: '' })}
                                    >
                                        <i className="fas fa-times me-1"></i>
                                        Clear Filters
                                    </Button>
                                    {can('Export Bidding Data') && (
                                        <Button 
                                            variant="success" 
                                            size="sm"
                                            onClick={handleExportBids}
                                            disabled={exporting}
                                        >
                                            {exporting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                    Exporting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download me-1"></i>
                                                    Export CSV
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Bids List - Card Layout for Better Sidebar Display */}
                            <div>
                                {bidsLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : allBids.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                                        <p className="mb-0">No bids found matching your criteria.</p>
                                    </div>
                                ) : (
                                    <>
                                        <Alert variant="info" className="mb-3">
                                            <i className="fas fa-eye-slash me-2"></i>
                                            <strong>Anonymous View:</strong> Bidder identities are hidden. To reveal a winner's details, close the auction and verify with the OTP sent to the highest bidder.
                                        </Alert>
                                        <div className="mb-3 text-muted small">
                                            Showing {allBids.length} bid{allBids.length !== 1 ? 's' : ''}
                                        </div>
                                        <div className="d-flex flex-column gap-3">
                                            {allBids.map(bid => (
                                                <div key={bid.id} className={`border rounded-lg p-3 ${bid.is_winner ? 'bg-warning bg-opacity-10 border-warning' : 'bg-light'}`}>
                                                    {/* Item Title and Status */}
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div>
                                                            <h6 className="mb-0 fw-bold text-dark">{bid.item_title || `Item #${bid.item_id}`}</h6>
                                                            <small className="text-muted">
                                                                Bid #{bid.bid_number || bid.id}
                                                            </small>
                                                        </div>
                                                        <div className="d-flex gap-1">
                                                            {bid.is_winner == 1 && (
                                                                <Badge bg="warning" text="dark">
                                                                    <i className="fas fa-trophy me-1"></i>
                                                                    Winner
                                                                </Badge>
                                                            )}
                                                            <Badge bg={bid.auction_closed ? 'secondary' : 'success'}>
                                                                {bid.auction_closed ? 'Closed' : 'Active'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Anonymous Bidder Info */}
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="fas fa-user-secret text-muted me-2"></i>
                                                        <div>
                                                            <span className="fw-semibold text-secondary">
                                                                {bid.bidder_type === 'employee' ? 'Employee Bidder' : 'Portal Bidder'}
                                                            </span>
                                                            <br />
                                                            <small className="text-muted fst-italic">Identity hidden</small>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Amount and Date Row */}
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <span className="text-muted small">Bid Amount:</span>
                                                            <span className="fw-bold ms-2" style={{ color: colors.primary, fontSize: '1.1rem' }}>
                                                                R {parseFloat(bid.amount || 0).toFixed(2)}
                                                            </span>
                                                        </div>
                                                        <div className="text-end">
                                                            <small className="text-muted">
                                                                {bid.created_at ? format(new Date(bid.created_at), 'dd MMM yyyy') : '-'}
                                                            </small>
                                                            <br />
                                                            <small className="text-muted">
                                                                {bid.created_at ? format(new Date(bid.created_at), 'HH:mm') : ''}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Winner verification status */}
                                                    {bid.is_winner == 1 && (
                                                        <div className="mt-2 pt-2 border-top">
                                                            {bid.winner_otp_verified ? (
                                                                <span className="badge bg-success">
                                                                    <i className="fas fa-check-circle me-1"></i>
                                                                    Winner Verified
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-warning text-dark">
                                                                    <i className="fas fa-clock me-1"></i>
                                                                    Awaiting OTP Verification
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {key === 'create' && canCreateItem && (
                    <Card className="border-0 shadow-sm rounded-xl bg-white">
                        <Card.Body className="p-4">
                            <Form onSubmit={handleCreate}>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="row g-3">
                                            <div className="col-8">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold text-dark small text-uppercase mb-1">Title</Form.Label>
                                                    <Form.Control type="text" required value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} placeholder="Item Title" />
                                                </Form.Group>
                                            </div>
                                            <div className="col-4">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold text-dark small text-uppercase mb-1">Category</Form.Label>
                                                    <Form.Select 
                                                        value={newItem.category_id} 
                                                        onChange={e => setNewItem({...newItem, category_id: e.target.value})}
                                                        required
                                                    >
                                                        <option value="">Select...</option>
                                                        {categories.map(cat => (
                                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <Form.Group>
                                            <Form.Label className="fw-bold text-dark small text-uppercase mb-1">Opening Bid (R)</Form.Label>
                                            <Form.Control type="number" step="0.01" required value={newItem.opening_bid} onChange={e => setNewItem({...newItem, opening_bid: e.target.value})} />
                                            <Form.Control 
                                                type="number" 
                                                step="0.01" 
                                                className="mt-1"
                                                placeholder="Increment Amount (Required)"
                                                required
                                                value={newItem.increment_amount || ''} 
                                                onChange={e => setNewItem({...newItem, increment_amount: e.target.value})} 
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group>
                                            <Form.Label className="fw-bold text-dark small text-uppercase mb-1">Opening Date</Form.Label>
                                            <div className="d-flex flex-column gap-1">
                                                <CustomDatePicker 
                                                    value={newItem.opening_date}
                                                    onChange={(val) => handleDateChange('opening_date', val)}
                                                    required
                                                />
                                                <Form.Control 
                                                    type="time" 
                                                    size="sm"
                                                    value={newItem.opening_date ? format(new Date(newItem.opening_date), 'HH:mm') : ''}
                                                    onChange={(e) => handleTimeChange('opening_date', e.target.value)}
                                                />
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group>
                                            <Form.Label className="fw-bold text-dark small text-uppercase mb-1">Closing Date</Form.Label>
                                            <div className="d-flex flex-column gap-1">
                                                <CustomDatePicker 
                                                    value={newItem.closing_date}
                                                    onChange={(val) => handleDateChange('closing_date', val)}
                                                    required
                                                    placement="right"
                                                />
                                                <Form.Control 
                                                    type="time" 
                                                    size="sm"
                                                    value={newItem.closing_date ? format(new Date(newItem.closing_date), 'HH:mm') : ''}
                                                    onChange={(e) => handleTimeChange('closing_date', e.target.value)}
                                                />
                                            </div>
                                        </Form.Group>
                                    </div>

                                    <div className="col-12">
                                        <Form.Group>
                                            <Form.Label className="fw-bold text-dark small text-uppercase mb-1">Description</Form.Label>
                                            <WysiwygEditor 
                                                value={newItem.description} 
                                                onChange={(val) => setNewItem({...newItem, description: val})}
                                                placeholder="Detailed description (paste screenshots here)..."
                                                minHeight="100px"
                                            />
                                        </Form.Group>
                                    </div>

                                    <div className="col-12">
                                        <Form.Group>
                                            <Form.Label className="fw-bold text-dark small text-uppercase mb-1">Photos</Form.Label>
                                            
                                            {/* Existing Photos */}
                                            {existingPhotos.length > 0 && (
                                                <div className="d-flex gap-2 mb-3 flex-wrap">
                                                    {existingPhotos.map(photo => (
                                                        <div key={photo.id} className="position-relative border rounded overflow-hidden" style={{ width: '100px', height: '100px' }}>
                                                            <img 
                                                                src={`${API_BASE_URL}${photo.photo_path}`} 
                                                                alt="Item" 
                                                                className="w-100 h-100 object-fit-cover" 
                                                            />
                                                            <button 
                                                                type="button"
                                                                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 p-0 d-flex align-items-center justify-content-center"
                                                                style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                                                                onClick={() => handleDeletePhoto(photo.id)}
                                                            >
                                                                <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <MultiFileInput 
                                                files={files} 
                                                onChange={setFiles}
                                                accept="image/*"
                                            />
                                        </Form.Group>
                                    </div>
                                    
                                    <div className="col-12 mt-4 d-flex gap-2 align-items-center">
                                        <div className="form-check form-switch">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="statusSwitch"
                                                checked={newItem.status === 'open'}
                                                onChange={e => setNewItem({...newItem, status: e.target.checked ? 'open' : 'inactive'})}
                                            />
                                            <label className="form-check-label small fw-bold text-uppercase" htmlFor="statusSwitch">
                                                {newItem.status === 'open' ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                        <Button variant="primary" type="submit" className="flex-grow-1 fw-bold shadow-sm py-2" disabled={creating} style={{ backgroundColor: colors.primary, borderColor: colors.primary }}>
                                            {creating ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {editingId ? 'Updating...' : 'Creating...'}
                                                </>
                                            ) : (editingId ? 'Update Bidding Item' : 'Create Bidding Item')}
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                )}

                {key === 'categories' && canManageCategories && (
                    <Card className="border-0 shadow-sm rounded-xl bg-white">
                        <Card.Body className="p-4">
                            <h5 className="font-bold text-dark mb-4">Manage Categories</h5>
                            <Form onSubmit={handleCreateCategory} className="mb-4">
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold text-dark small text-uppercase mb-1">New Category Name</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control 
                                            type="text" 
                                            required 
                                            value={newCategory} 
                                            onChange={e => setNewCategory(e.target.value)} 
                                            placeholder="e.g. Office Supplies" 
                                        />
                                        <Button variant="primary" type="submit" className="fw-bold shadow-sm px-4" style={{ backgroundColor: colors.primary, borderColor: colors.primary }}>
                                            Add
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Form>
                            
                            <div className="border rounded-lg overflow-hidden">
                                <table className="table table-hover mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0 py-3 px-4 text-secondary font-semibold text-sm">Name</th>
                                            <th className="border-0 py-3 px-4 text-secondary font-semibold text-sm text-end">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map(cat => (
                                            <tr key={cat.id}>
                                                <td className="py-3 px-4 text-dark">{cat.name}</td>
                                                <td className="py-3 px-4 text-muted text-end text-sm">
                                                    {cat.created_at ? new Date(cat.created_at).toLocaleDateString() : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                        {categories.length === 0 && (
                                            <tr>
                                                <td colSpan="2" className="text-center py-4 text-muted">No categories found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </Offcanvas.Body>
        </Offcanvas>

        {/* Bid Modal */}
        <Modal show={!!selectedItem && !showOtpModal} onHide={() => setSelectedItem(null)}>
            <Modal.Header closeButton>
                <Modal.Title>Place Bid: {selectedItem?.title}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleBidSubmit}>
                <Modal.Body>
                    <div className="mb-4 bg-light p-3 rounded border">
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Opening Bid:</span>
                            <span className="fw-bold">R {parseFloat(selectedItem?.opening_bid || 0).toFixed(2)}</span>
                        </div>
                        {selectedItem?.highest_bid && parseFloat(selectedItem.highest_bid) > 0 && (
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Current Highest Bid:</span>
                                <span className="fw-bold text-primary">R {parseFloat(selectedItem.highest_bid).toFixed(2)}</span>
                            </div>
                        )}
                        <div className="d-flex justify-content-between">
                            <span className="text-muted">Minimum Increment:</span>
                            <span className="fw-bold">R {parseFloat(selectedItem?.increment_amount || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    <Form.Group>
                        <Form.Label className="fw-bold">Your Bid Amount (R)</Form.Label>
                        <div className="input-group mb-2">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => {
                                    const current = parseFloat(bidAmount) || 0;
                                    const increment = parseFloat(selectedItem?.increment_amount) || 0;
                                    const opening = parseFloat(selectedItem?.opening_bid) || 0;
                                    const highest = parseFloat(selectedItem?.highest_bid) || 0;
                                    
                                    const minBid = highest > 0 ? highest + increment : opening;
                                    const newVal = Math.max(minBid, current - increment);
                                    setBidAmount(newVal.toFixed(2));
                                }}
                            >
                                <i className="fas fa-minus"></i>
                            </Button>
                            <Form.Control 
                                type="number" 
                                step="0.01" 
                                required 
                                min={selectedItem?.highest_bid > 0 ? parseFloat(selectedItem.highest_bid) + parseFloat(selectedItem.increment_amount) : selectedItem?.opening_bid} 
                                value={bidAmount} 
                                onChange={e => setBidAmount(e.target.value)} 
                                className="text-center fw-bold fs-5"
                            />
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => {
                                    const current = parseFloat(bidAmount) || 0;
                                    const increment = parseFloat(selectedItem?.increment_amount) || 0;
                                    setBidAmount((current + increment).toFixed(2));
                                }}
                            >
                                <i className="fas fa-plus"></i>
                            </Button>
                        </div>
                        <Form.Text className="text-muted small">
                            <i className="fas fa-info-circle me-1"></i>
                            Use the +/- buttons to adjust your bid by the required increment.
                        </Form.Text>
                    </Form.Group>

                    {/* Disclaimer Acceptance - Only show if not yet accepted for this item */}
                    {biddingSettings?.site_bid_disclaimer && !hasAcceptedDisclaimerForItem && (
                        <Form.Group className="mt-3">
                            <div className="bg-light border border-warning p-3 rounded-lg">
                                <div className="mb-2">
                                    <strong className="text-warning">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        Disclaimer
                                    </strong>
                                </div>
                                <div 
                                    className="small text-dark mb-3 p-2 bg-white rounded" 
                                    style={{ maxHeight: '150px', overflowY: 'auto' }}
                                >
                                    {biddingSettings.site_bid_disclaimer}
                                </div>
                                <div 
                                    className={`d-flex align-items-center w-100 p-2 rounded border cursor-pointer ${disclaimerAccepted ? 'bg-success bg-opacity-10 border-success' : 'bg-white border-secondary border-opacity-25'}`}
                                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                    onClick={() => setDisclaimerAccepted(!disclaimerAccepted)}
                                >
                                    <input 
                                        className="form-check-input me-2" 
                                        type="checkbox" 
                                        id="disclaimerAccepted"
                                        checked={disclaimerAccepted}
                                        onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                        style={{ marginTop: 0 }}
                                        required={!hasAcceptedDisclaimerForItem}
                                    />
                                    <label className="fw-semibold mb-0 small" htmlFor="disclaimerAccepted" style={{ cursor: 'pointer' }}>
                                        I have read and accept the disclaimer
                                    </label>
                                </div>
                            </div>
                        </Form.Group>
                    )}

                    <Form.Group className="mt-3">\n                        <div 
                            className={`d-flex align-items-center w-100 p-3 rounded-lg border ${notifyOutbid ? 'bg-primary bg-opacity-10 border-primary' : 'bg-light border-secondary border-opacity-25'}`}
                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                            onClick={() => setNotifyOutbid(!notifyOutbid)}
                        >
                            <input 
                                className="form-check-input me-3" 
                                type="checkbox" 
                                id="notifyOutbid"
                                checked={notifyOutbid}
                                onChange={(e) => setNotifyOutbid(e.target.checked)}
                                style={{ marginTop: 0, width: '20px', height: '20px' }}
                            />
                            <div className="flex-grow-1">
                                <label className="fw-semibold mb-0 d-block" htmlFor="notifyOutbid" style={{ cursor: 'pointer' }}>
                                    <i className="fas fa-bell me-2 text-warning"></i>
                                    Notify me if I am outbid
                                </label>
                                <small className="text-muted d-block mt-1">
                                    Receive an SMS alert when someone places a higher bid on this item
                                </small>
                            </div>
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedItem(null)} disabled={submitting}>Cancel</Button>
                    <Button 
                        variant="primary" 
                        type="submit" 
                        style={{ backgroundColor: colors.primary, borderColor: colors.primary }} 
                        disabled={submitting || (biddingSettings?.site_bid_disclaimer && !hasAcceptedDisclaimerForItem && !disclaimerAccepted)}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Submitting...
                            </>
                        ) : 'Submit Bid'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>

        {/* OTP Modal */}
        <Modal show={showOtpModal} onHide={() => setShowOtpModal(false)} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Verify Bid</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center py-4">
                <div className="mb-4">
                    <div className="text-muted small text-uppercase fw-bold mb-1">Confirming Bid For</div>
                    <h5 className="mb-1">{selectedItem?.title}</h5>
                    <div className="text-primary fw-bold fs-4">R {parseFloat(bidAmount || 0).toFixed(2)}</div>
                </div>

                <p className="text-muted mb-4">
                    An OTP has been sent to your phone.<br/>
                    Please enter it below to confirm your bid.
                </p>
                
                <Form.Group className="mb-3 d-flex justify-content-center">
                    <Form.Control 
                        type="text" 
                        value={otp} 
                        onChange={(e) => setOtp(e.target.value)} 
                        placeholder="000000"
                        className="text-center fw-bold fs-3"
                        style={{ maxWidth: '200px', letterSpacing: '0.2em' }}
                        maxLength={6}
                        autoFocus
                    />
                </Form.Group>
                
                <div className="text-muted small">
                    Didn't receive code? <Button variant="link" className="p-0 text-decoration-none small" onClick={handleBidSubmit} disabled={submitting}>Resend OTP</Button>
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-center border-0 pb-4">
                <Button 
                    variant="primary" 
                    size="lg" 
                    className="px-5 rounded-pill shadow-sm"
                    onClick={handleVerifyOtp} 
                    style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
                    disabled={verifying || !otp || otp.length < 4}
                >
                    {verifying ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Verifying...
                        </>
                    ) : 'Confirm Bid'}
                </Button>
            </Modal.Footer>
        </Modal>
        {/* Image Viewer Modal */}
        <Modal show={!!viewingImagesItem} onHide={() => setViewingImagesItem(null)} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{viewingImagesItem?.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0 bg-dark">
                {viewingImagesItem?.photos && viewingImagesItem.photos.length > 0 ? (
                    <Carousel interval={null}>
                        {viewingImagesItem.photos.map((photo, index) => (
                            <Carousel.Item key={index}>
                                <div className="d-flex align-items-center justify-content-center" style={{ height: '500px', backgroundColor: '#000' }}>
                                    <img
                                        className="d-block"
                                        src={`${API_BASE_URL}${photo.photo_path}`}
                                        alt={`Slide ${index + 1}`}
                                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                </div>
                            </Carousel.Item>
                        ))}
                    </Carousel>
                ) : (
                    <div className="text-center text-white p-5">No images available</div>
                )}
            </Modal.Body>
        </Modal>
        
        {/* Winner OTP Verification Modal */}
        <Modal show={showWinnerOtpModal} onHide={() => { setShowWinnerOtpModal(false); setRevealedWinner(null); }} centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title>
                    <i className="fas fa-key text-warning me-2"></i>
                    Verify Winning Bid
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                {selectedWinnerItem && (
                    <div className="mb-4 p-3 bg-light rounded">
                        <h6 className="fw-bold mb-1">{selectedWinnerItem.title}</h6>
                        <small className="text-muted">
                            Winning Amount: <strong className="text-primary">R {parseFloat(selectedWinnerItem.winner_amount || 0).toFixed(2)}</strong>
                        </small>
                    </div>
                )}
                
                {!revealedWinner ? (
                    <>
                        <p className="text-muted mb-3">
                            Enter the OTP that was sent to the highest bidder to reveal their identity. 
                            The bidder must provide this code to you.
                        </p>
                        
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Winner OTP</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={winnerOtp} 
                                onChange={(e) => setWinnerOtp(e.target.value)} 
                                placeholder="Enter 6-digit OTP"
                                className="text-center fw-bold fs-4"
                                style={{ letterSpacing: '0.2em' }}
                                maxLength={6}
                                autoFocus
                            />
                            <Form.Text className="text-muted">
                                This OTP was sent via SMS to the winning bidder when the auction closed.
                            </Form.Text>
                        </Form.Group>
                    </>
                ) : (
                    <div className="text-center">
                        <div className="mb-4">
                            <div className="d-inline-flex align-items-center justify-content-center bg-success bg-opacity-10 rounded-circle mb-3" style={{ width: '80px', height: '80px' }}>
                                <i className="fas fa-trophy text-success fa-2x"></i>
                            </div>
                            <h5 className="fw-bold text-success mb-1">Winner Verified!</h5>
                        </div>
                        
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <div className="mb-3">
                                    <small className="text-muted text-uppercase">Bidder Type</small>
                                    <div className="fw-semibold">
                                        {revealedWinner.type === 'employee' ? (
                                            <><i className="fas fa-id-badge text-info me-2"></i>Employee</>
                                        ) : (
                                            <><i className="fas fa-user text-primary me-2"></i>Portal User</>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <small className="text-muted text-uppercase">Name</small>
                                    <div className="fw-bold fs-5">{revealedWinner.name}</div>
                                </div>
                                
                                {revealedWinner.employee_code && (
                                    <div className="mb-3">
                                        <small className="text-muted text-uppercase">Employee Code</small>
                                        <div className="fw-semibold">{revealedWinner.employee_code}</div>
                                    </div>
                                )}
                                
                                {revealedWinner.email && (
                                    <div className="mb-3">
                                        <small className="text-muted text-uppercase">Email</small>
                                        <div className="fw-semibold">{revealedWinner.email}</div>
                                    </div>
                                )}
                                
                                {revealedWinner.phone && (
                                    <div className="mb-3">
                                        <small className="text-muted text-uppercase">Phone</small>
                                        <div className="fw-semibold">{revealedWinner.phone}</div>
                                    </div>
                                )}
                                
                                <div className="pt-3 border-top">
                                    <div className="row">
                                        <div className="col-6">
                                            <small className="text-muted text-uppercase">Bid Number</small>
                                            <div className="fw-semibold">{revealedWinner.bid_number || revealedWinner.bid_id}</div>
                                        </div>
                                        <div className="col-6">
                                            <small className="text-muted text-uppercase">Amount</small>
                                            <div className="fw-bold text-primary">R {parseFloat(revealedWinner.amount || 0).toFixed(2)}</div>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                {!revealedWinner ? (
                    <>
                        <Button variant="secondary" onClick={() => setShowWinnerOtpModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleVerifyWinnerOtp}
                            disabled={verifyingWinner || !winnerOtp || winnerOtp.length < 6}
                            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
                        >
                            {verifyingWinner ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-unlock me-2"></i>
                                    Reveal Winner
                                </>
                            )}
                        </Button>
                    </>
                ) : (
                    <Button variant="secondary" onClick={() => { setShowWinnerOtpModal(false); setRevealedWinner(null); }}>
                        Close
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default BiddingPanel;
